import React from 'react';
import { Alert, AlertTitle, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useRuleTenantStatus } from '../../hooks/useRuleTenantStatus';
import { canAdminRules } from '../../utils/capabilities';
import { useAuth } from '../../hooks/useAuth';

/**
 * Persistent tenant-wide rule health banner (ADR-022 §9). Rendered inside MainLayout and visible to
 * ALL users (not only rule admins) when the tenant rate ceiling is tripped, so affected users
 * understand why automation is paused. Operational controls (untrip, kill) live on the admin-gated
 * Rules admin page; this banner only links there.
 *
 * Fully feature-gated: the underlying status hook makes zero requests when rules are disabled,
 * unauthenticated, or tenant context is absent.
 */
const RulesBanner: React.FC = () => {
  const { phase, status } = useRuleTenantStatus();
  const navigate = useNavigate();
  const { user } = useAuth();

  if (phase !== 'ready' || !status) {
    return null;
  }
  if (!status.tripped) {
    return null;
  }

  const isAdmin = canAdminRules(user?.roles);
  return (
    <Alert
      severity="error"
      sx={{ mb: 2 }}
      action={
        isAdmin ? (
          <Button color="inherit" size="small" onClick={() => navigate('/rules')}>
            Manage rules
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>Automation paused: tenant rate ceiling tripped</AlertTitle>
      <Typography variant="body2">
        Rule dispatch is paused for this tenant until an administrator clears the rate ceiling.
      </Typography>
    </Alert>
  );
};

export default RulesBanner;
